<template>
	<div
			class="h-100 w-100"
			@dragover.prevent="dragOver"
			@dragleave.prevent="dragLeave"
			@drop.prevent="drop"
	>
		<!-- NO CURRENT IMAGE / PLACEHOLDER WHILE MEDIA MANAGER IS OPEN -->
		<div
				v-if="!localValue || localValue?.length === 0"
				class="align-center d-flex flex-column h-100 imageUploader justify-center w-100 dropzone pb-6 pt-4"
				:class="{
					'dropzone--active': dragging && !dropped,
					'dropzone--uploading': dropped
				}"
				@click="chooseImage"
		>

			<v-btn
					v-if="!dragging && !dropped"
					class="mt-n1 me-n2 position-absolute z-3"
					color="black"
					depressed
					icon
					style="top: 1rem; right: 1.25rem;"
					@click.stop="$emit('canceled')"
			>
				<v-icon>mdi-close</v-icon>
			</v-btn>

			<v-progress-linear
					v-if="dropped"
					absolute
					color="primary"
					indeterminate
					rounded
					style="width: 60%; left: 20%;"
			></v-progress-linear>

			<template v-if="showTab === 'image'">
				<v-icon
						color="grey darken-4"
						:size="$vuetify.breakpoint.mdAndUp ? 100 : 50"
						style="pointer-events: none;"
						:style="{ opacity: !dragging ? 1 : 0 }"
				>
					mdi-image-filter-hdr
				</v-icon>
			</template>
			<template v-if="showTab === 'audio'">
				<v-icon
						color="grey darken-4"
						:size="$vuetify.breakpoint.mdAndUp ? 100 : 50"
						style="pointer-events: none;"
						:style="{ opacity: !dragging ? 1 : 0 }"
				>
					mdi-volume-high
				</v-icon>
			</template>
			<template v-if="showTab === 'video'">
				<v-icon
						color="grey darken-4"
						:size="$vuetify.breakpoint.mdAndUp ? 100 : 50"
						style="pointer-events: none;"
						:style="{ opacity: !dragging ? 1 : 0 }"
				>
					mdi-video-outline
				</v-icon>
			</template>

			<div v-if="minDimensions" class="pb-2 text-caption">
				<span class="font-weight-bold">Minimum image size:</span> {{ minDimensions }}
			</div>

			<div style="pointer-events: none;" :style="{ opacity: !dragging ? 1 : 0 }">
				<v-btn
						class="mb-0 rounded-lg"
						color="secondary"
						depressed
						small
				>
					{{ $vuetify.breakpoint.smAndUp ? `Browse/Upload ${ mediaName }` : `Upload ${ mediaName }` }}
				</v-btn>
			</div>

			<div
					class="mt-4 grey--text text--darken-1 text-body-2"
					:style="{ opacity: !dragging ? 1 : 0 }"
			>
				Or Drag Your File Here
			</div>
		</div>

		<!-- IMAGE CROPPER -->
		<v-row v-show="imagePreviews.length > 0" id="imageUploader">
			<v-col>
				<!-- CROPPER / PREVIEWS -->
				<v-row>
					<v-col cols="12" md="7">
						<v-row>
							<v-col>
								<h6>Image Cropper</h6>
							</v-col>
						</v-row>
						<v-row>
							<v-col class="pt-0" cols="12">
								<div class="d-flex">
									<!-- Zoom In -->
									<v-btn
											class="me-1"
											color="secondary"
											depressed
											fab
											outlined
											small
											style="border-style: solid;"
											@click="toolBarAction('zoom', 0.1)"
									>
										<v-icon small>mdi-magnify-plus-outline</v-icon>
									</v-btn>

									<!-- Zoom Out -->
									<v-btn
											color="secondary"
											depressed
											fab
											outlined
											small
											style="border-style: solid;"
											@click="toolBarAction('zoom', -0.1)"
									>
										<v-icon small>mdi-magnify-minus-outline</v-icon>
									</v-btn>

									<v-divider
											class="mx-2 my-1"
											vertical
									></v-divider>

									<!-- Flip Horizontal -->
									<v-btn
											class="me-1"
											color="secondary"
											depressed
											fab
											outlined
											small
											style="border-style: solid;"
											@click="toolBarAction('scaleX', scaleX)"
									>
										<v-icon small>mdi-arrow-left-right</v-icon>
									</v-btn>

									<!-- Flip Vertical -->
									<v-btn
											color="secondary"
											depressed
											fab
											outlined
											small
											style="border-style: solid;"
											@click="toolBarAction('scaleY', scaleY)"
									>
										<v-icon small>mdi-arrow-up-down</v-icon>
									</v-btn>

									<v-divider
											class="mx-2 my-1"
											vertical
									></v-divider>

									<!-- Rotate Left -->
									<v-btn
											class="me-1"
											color="secondary"
											depressed
											fab
											outlined
											small
											style="border-style: solid;"
											@click="toolBarAction('rotate', -45)"
									>
										<v-icon small>mdi-rotate-left</v-icon>
									</v-btn>

									<!-- Rotate Right -->
									<v-btn
											color="secondary"
											depressed
											fab
											outlined
											small
											style="border-style: solid;"
											@click="toolBarAction('rotate', 45)"
									>
										<v-icon small>mdi-rotate-right</v-icon>
									</v-btn>
								</div>
							</v-col>
						</v-row>

						<v-divider class="my-4"></v-divider>
						<div class="cropper-container">
							<img
									ref="displayImg"
									:src="cropperPreview"
									class="imageUploader__image"
							/>
						</div>
					</v-col>
					<v-col cols="12" md="5">
						<v-row>
							<v-col>
								<h6>Image Preview</h6>
							</v-col>
						</v-row>
						<v-row>
							<v-col class="pt-0" cols="12">
								<div class="d-flex">
									<v-btn
											aria-label="Change Image"
											color="secondary"
											depressed
											fab
											small
											@click="removeImage(0)"
									>
										<v-icon small>mdi-undo-variant</v-icon>
									</v-btn>
									<v-divider
											class="mx-2 my-1"
											vertical
									></v-divider>
									<v-btn
											aria-label="Accept"
											color="success"
											depressed
											fab
											small
											@click="acceptImage()"
									>
										<v-icon small>mdi-check</v-icon>
									</v-btn>
								</div>
							</v-col>
						</v-row>
						<v-divider class="my-4"></v-divider>

						<v-row class="text-center">
							<v-col cols="12">
								<v-img
										class="mx-auto"
										contain
										:max-width="480"
										:src="cropperDestination"
								></v-img>
							</v-col>
						</v-row>
					</v-col>
				</v-row>
			</v-col>
		</v-row>

		<bw-media-manager
				v-if="dialog.showMediaManager"
				:entity="uploadAssignment"
				:entity-id="this.uploadAssignmentId||0" :is-admin="this.isAdmin"
				:nav-search="{entity: uploadAssignment, itemId:this.uploadAssignmentId||0}"
				:super-admin="this.isSuperAdmin"
				:min-dimensions="minDimensions"
				enable-tabs="image"
				show-tab="image"
				@cancel="$emit('canceled')"
				@file-chosen="file => fileChanged({ target: { files: [file] } })"
		>
		</bw-media-manager>
	</div>
</template>

<script>
import Cropper from 'cropperjs'
import $api from '@api/api';
import BwMediaManager from "./BwMediaManager";

export default {
	name: 'BwImageUpload',
	components: {
		BwMediaManager
	},
	inheritAttrs: false,
	props: {
		// NumberxNumber
		// i.e. 200x300
		dimensions: {
			type: [String, Boolean],
			default: false,
		},
		isAdmin: {
			type: Boolean,
			default: false
		},
		isSuperAdmin: {
			type: Boolean,
			default: false
		},
		maxDimensions: {
			type: [String, Boolean],
			default: false,
		},
		maxFiles: {
			type: Number,
			default: 1,
		},
		// kb, mb, or gb (enforced as kib, mib, gib)
		maxFileSize: {
			type: [String, Boolean],
			default: false,
		},
		minDimensions: {
			type: [String, Boolean],
			default: false,
		},
		minFileSize: {
			type: [String, Boolean],
			default: false,
		},
		multiple: {
			type: Boolean,
			default: false,
		},
		// square, landscape, or portrait
		orientation: {
			type: String,
			default: 'any',
		},
		cropperSettings: {
			type: Object,
			default: () => ({})
		},
		uniqueId: {
			type: [Number, String],
			default: 1
		},
		uploadAssignment: {
			type: String,
		},
		uploadAssignmentId: {
			type: [Number, String],
		},
		value: {
			type: undefined,
			required: true,
		}
	},
	inject: ['showTab'],
	data() {
		return {
			cloudinary: null,
			cropper: {},
			cropperDestination: {},
			cropperPreview: null,
			dialog: {
				showMediaManager: false
			},
			dragging: false,
			dropped: false,
			errorMessages: [],
			imagePreviews: [],
			loading: true,
			scaleX: -1,
			scaleY: -1
		}
	},
	computed: {
		localValue: {
			get() {
				return this.value || []
			},
			set(v) {
				this.makeImagePreviews(v)

				this.$emit('input', v)
			},
		},
		computedAR() {
			if(this.multiple) return 1

			return undefined
		},
		mediaName() {
			switch (this.showTab) {
				case 'image':
					return 'Images';
				case 'video':
					return 'Video';
				case 'audio':
					return 'Audio';
				default:
					return 'Media'
			}
		},
		previewImageCols() {
			if(this.multiple)
				return {
					cols: 6,
					md: 4,
				}

			return {
				cols: 12,
			}
		}
	},
	mounted() {
		this.cloudinary = window.cloudinary;

		window.EventBus.$on('media-manager-dialog', (key) => {
			if(key === 'cancel') this.imagePreviews = [];
			this.dialog.showMediaManager = false;
		});
		window.EventBus.$on('image-recrop', () => {
			// this.resetCropper();
		});

		for (let file of this.localValue) {

			// Initialize cropperSettings if it doesn't exist
			if(!file.cropperSettings) {
				file.cropperSettings = {};
			}

			// transpose from media db to cropper js
			file.cropperSettings.x = file.x || file?.cropperSettings.x || 0;
			file.cropperSettings.y = file.y || file.cropperSettings.y || 0;
			file.cropperSettings.width = file.width || file.cropperSettings.width;
			file.cropperSettings.height = file.height || file.cropperSettings.height;
			file.cropperSettings.rotate = file.rotation || file.cropperSettings.rotation || file.cropperSettings.rotate || 0;
			file.cropperSettings.scaleX = file.scale_x || file.cropperSettings.scale_x || file.cropperSettings.scaleX || 1;
			file.cropperSettings.scaleY = file.scale_y || file.cropperSettings.scale_y || file.cropperSettings.scaleY || 1;
		}

		this.makeImagePreviews(this.localValue);
		this.loading = false;
	},
	methods: {
		acceptImage() {
			this.$emit('canceled');
		},
		append() {
			// this.$refs.fileInput.click()
		},
		apply() {
			for (let file of this.localValue) {
				if(!file.originalURL) file.originalURL = file.url || '';

				if (this.showTab !== 'image') continue;
				// if it's a cloudinary image then change the url
				if(file.originalURL.indexOf("cloudinary.com") > -1 && file.cropperSettings) {
					let i = file.originalURL.lastIndexOf("/upload/");

					// let aspectRatio = this.chosenFile.width / this.chosenFile.height;
					let url = file.originalURL.substring(0, i + 8);
					url += "x_" + Math.round(file.cropperSettings.x) + ",";
					url += "y_" + Math.round(file.cropperSettings.y) + ",";
					url += "w_" + Math.round(file.cropperSettings.width) + ",";
					url += "h_" + Math.round(file.cropperSettings.height) + ",";

					if(file.cropperSettings.rotate) {
						url += "a_" + Math.round(file.cropperSettings.rotate) + ",";
					}
					file.cropperSettings.rotation = file.cropperSettings.rotate; // transpose to media db
					// horizontal flip, cloudinary is opposite cropper js
					if(file.cropperSettings.scaleX < 0) url += "a_hflip,";
					file.cropperSettings.scale_x = file.cropperSettings.scaleX || 1; // transpose to media db

					// vertical flip, cloudinary is opposite cropper js
					if(file.cropperSettings.scaleY < 0) url += "a_vflip,";
					file.cropperSettings.scale_y = file.cropperSettings.scaleY || 1; // transpose to media db

					url += "c_crop";
					file.url = url + file.originalURL.substring(i + 7);
				}
			}
			this.$emit('applied', this.localValue);
		},
		chooseImage() {
			this.dialog.showMediaManager = true;
		},
		dragLeave() {
			this.dragging = false;
		},
		dragOver() {
			this.dragging = true;
		},
		async drop(e) {
			this.dropped = true;

			let file;
			const entity = this.uploadAssignment || 0;
			const entityId = this.uploadAssignmentId || 0;

			let formData = new FormData();
			formData.append('cloud_name', 'Bandwango');
			formData.append('file', e.dataTransfer.files[0]);
			formData.append('folder', `partners/${entity}/${entityId}`);
			formData.append('upload_preset', 'Partner Files');

			try {
				const { data } = await axios.post(
						'https://api.cloudinary.com/v1_1/Bandwango/upload',
						formData,
						{
							headers: {
								'Content-Type': 'multipart/form-data'
							},
							transformRequest: [
								(data, headers) => {
									delete headers['X-Socket-Id'];
									delete headers.common['BW-Admin-Partner-Id'];
									return data;
								}
							]
						}
				);

				file = {
					...data,
					'upload_assignment': entity || 0,
					'upload_assignment_id': entityId || 0,
				};

				try {
					const { data: { data } } = await axios.post(`/cloudMedia`, file);
					await this.fileChanged({ target: { files: [data] } });
				}
				catch (error) {
					console.error('Error creating cloud media:', error);
				}
			}
			catch (error) {
				console.error('Error uploading the file:', error);
			}

			this.dragging = false;
			this.dropped = false;
		},
		async fileChanged(e) {
			if(e.target.files.length === 0) return;

			e.target.files.forEach(file => {
				this.$set(file, 'cropperSettings', this.cropperSettings);
			});

			this.dialog.showMediaManager = false;

			this.errorMessages = [];
			let files;
			if(this.multiple) {
				if(this.localValue.length + e.target.files.length > this.maxFiles) {

					window.EventBus.$emit('system-messages', [
						{
							message: `Maximum number of files allowed is ${this.maxFiles}`,
							messageType: 'error'
						}
					]);
				}
				else {
					files = [...this.localValue, ...e.target.files];
				}
			}
			else {
				files = [e.target.files[0]];
			}

			if(await this.validate(files)) {
				this.localValue = files;
				// save the file references to the server
				const saveFiles = files.filter(x => !x.id);
				if(saveFiles.length) {
					const medias = (await $api.medias.addMedia(saveFiles, {
						upload_assignment_id: this.uploadAssignmentId,
						upload_assignment: this.uploadAssignment
					})).data;
					for (let media of medias) {
						const i = this.localValue.findIndex(x => x.name === media.original_file_name);
						if(i > -1) this.localValue[i] = { ...this.localValue[i], ...media };
					}
				}
			}
		},

		getImageDimensions(file) {
			return new Promise(function (resolve, reject) {
				const img = new Image()
				img.onload = function () {
					resolve([img.width, img.height]);
				}
				img.onerror = function () {
					reject(img)
				}
				img.src = file.src
			})
		},

		getValidationDimensions() {
			if([
				this.dimensions,
				this.minDimensions,
				this.maxDimensions
			].every((v) => v === false)) {
				return false;
			}

			let dims, operator
			if(this.dimensions) {
				operator = '=';
				dims = this.dimensions;
			}
			else if(this.minDimensions) {
				operator = '<=';
				dims = this.minDimensions;
			}
			else if(this.maxDimensions) {
				operator = '>=';
				dims = this.maxDimensions;
			}

			return {
				requirements: dims,
				operator,
			};
		},
		loadCropper(file) {
			const { cropperSettings = {} } = this.localValue[0];
			const mimeType = file.type;
			const aspectRatio = cropperSettings.limitAR
					? eval(cropperSettings.limitAR.replace(':', '/'))
					: (16 / 9);
			// const fileType = (file.original_file_name || file.name || '').split('.').pop()

			this.$nextTick(() => {
				this.cropper = new Cropper(this.$refs.displayImg, {
					aspectRatio,
					autoCropArea: 1,
					background: false,
					cropBoxMovable: true,
					cropBoxResizable: true,
					dragMode: 'move',
					setCropBoxData: { width: '800' },
					data: cropperSettings,
					crop: (e) => {
						const cropperCanvas = this.cropper.getCroppedCanvas({ maxWidth: 800 });
						this.cropperDestination = cropperCanvas.toDataURL(mimeType)
						this.localValue[0].cropperSettings = {
							...cropperSettings,
							...e.detail,
						};
						this.apply();
					},
					ready: this.crop,
					viewMode: 1,
					zoomOnWheel: false,
				});
				this.apply();
			})
		},
		makeImagePreviews(files) {
			if (this.showTab !== 'image') {
				if (files.length > 0)
					this.$nextTick(() => {
						this.$emit('applied', this.localValue);
					});
				return;
			}

			const res = Array.from(files).map(async (file) => {
				return {
					name: file.name,
					type: file.type,
					src: file.originalURL || file.url || await this.readAsDataURL(file),
				};
			});

			Promise.all(res).then((r) => {
				this.imagePreviews = r;
				if(r.length > 0) {
					// TODO: support for multiple images
					this.cropperPreview = r[0].src;
					this.loadCropper(r[0]);
				}
			})
		},
		readAsDataURL(file) {
			return new Promise(function (resolve, reject) {
				const fr = new FileReader()
				fr.onload = function () {
					resolve(fr.result);
				}
				fr.onerror = function () {
					reject(fr);
				}
				fr.readAsDataURL(file);
			})
		},
		removeImage(index) {
			this.localValue = this.localValue.filter((v, i) => i !== index); // filter instead of splice so the set is triggered
			// this.$refs.fileInput.value = null;
			this.cropper.destroy();
		},
		resetCropper() {
			this.cropper?.destroy();
			this.makeImagePreviews(this.localValue);

			this.apply();
		},
		toolBarAction(method, option = false) {
			// Method selection
			switch (method) {
				case "zoom":
					this.cropper.zoom(option);
					break;
				case "setDragMode":
					this.cropper.setDragMode(option);
					break;
				case "rotate":
					this.cropper.rotate(option);
					break;
				case "scaleX":
					this.cropper.scaleX(option);
					this.scaleX = -this.scaleX;
					break;
				case "scaleY":
					this.cropper.scaleY(option);

					this.scaleY = -this.scaleY;
					break;
				case "setAspectRatio":
					this.cropper.setAspectRatio(option);
					break;
				case "crop":
					this.cropper.crop();
					break;
				case "clear":
					this.cropper.clear();
					break;
			}
			this.apply();
		},
		async validate(files) {
			const type = this.showTab;
			// dimensions and orientation
			const validImageSizes = type === 'image' ?
				await this.validateImageSizes(files)
				: true;
			const validFileSizes = this.validateFileSizes(files);

			const isValid = [validImageSizes, validFileSizes].every((v) => v === true);

			// this.$refs.provider.applyResult({
			//   errors: this.errorMessages, // array of string errors
			//   valid: isValid, // boolean state
			//   failedRules: {}, // should be empty since this is a manual error.
			// })
			window.EventBus.$emit('system-messages', this.errorMessages.map(x => {
				return { message: x, messageType: 'error' };
			}));

			return isValid;
		},
		validateFileSizes(files) {
			if(!this.maxFileSize && !this.minFileSize) return true;

			const validArr = [];
			const powers = {
				kb: 1,
				mb: 2,
				gb: 3,
			};

			const convertBytes = (file, power) => {
				return file / Math.pow(1024, power)
			}

			files.forEach((file) => {
				if(this.minFileSize) {
					const size = this.minFileSize.slice(0, -2);
					const suffix = this.minFileSize.slice(-2);
					if(
							convertBytes(file.size, powers[suffix.toLowerCase()]) <
							Number(size.trim())
					) {
						validArr.push(false);
						this.errorMessages.push(`File is too small`);
					}
					else {
						validArr.push(true)
					}
				}
				if(this.maxFileSize) {
					const size = this.maxFileSize.slice(0, -2);
					const suffix = this.maxFileSize.slice(-2);
					if(
							convertBytes(file.size, powers[suffix.toLowerCase()]) >
							Number(size.trim())
					) {
						validArr.push(false);
						this.errorMessages.push(`File is too large`);
					}
					else {
						validArr.push(true);
					}
				}
			})

			return validArr.every((v) => v === true)
		},
		validateImageSizes(files) {
			const checkDimensions = this.getValidationDimensions();
			const checkOrientation = this.orientation !== 'any';

			const promises = Array.from(files).map(async (file) => {
				const imageObj = {
					name: file.name,
					src: file.originalURL || file.url || await this.readAsDataURL(file),
				};
				return imageObj;
			})

			return Promise.all(promises).then(async (files) => {
				const validArr = [];
				for (const file of files) {
					const [imgWidth, imgHeight] = await this.getImageDimensions(file);
					// validate dimensions
					if(checkDimensions) {
						const { operator, requirements } = checkDimensions;
						const [width, height] = requirements.split('x');
						if(
								eval(`${width}${operator}${imgWidth}`) && // eslint-disable-line no-eval
								eval(`${height}${operator}${imgHeight}`) // eslint-disable-line no-eval
						) {
							validArr.push(true);
						}
						else {
							validArr.push(false);
							this.errorMessages.push(
									`Image dimensions don't meet requirements`
							);
						}
					}
					// validate orientation
					if(checkOrientation) {
						const validOrientations = {
							landscape: ['landscape', 'square'],
							portrait: ['portrait', 'square'],
							square: ['square'],
						};

						let imgOrientation;
						if(imgWidth === imgHeight) imgOrientation = 'square';
						else if(imgWidth > imgHeight) imgOrientation = 'landscape';
						else imgOrientation = 'portrait';

						if(validOrientations[this.orientation].includes(imgOrientation)) {
							validArr.push(true);
						}
						else {
							validArr.push(false);
							this.errorMessages.push(
									`Image orientation must be ${this.orientation}`
							);
						}
					}
				}
				return validArr.every((v) => v === true);
			});
		}
	},
}
</script>

<style lang="scss" scoped>
@import '~vuetify/src/styles/main.sass';

.dropzone {
	position: relative;
	z-index: 2;

	&::before, &::after {
		border-radius: 1rem;
		bottom: 0;
		content: '';
		left: 0;
		position: absolute;
		right: 0;
		top: 0;
		z-index: 1;
	}

	&::before {
		background-color: rgba(0, 0, 0, 0.02);
	}

	&::after {
		border: dashed 4px rgba(0, 0, 0, 0.06);
	}

	&--active::before {
		background-color: var(--v-primary-base);
		opacity: 0.04;
	}

	&--active::after {
		align-items: center;
		border: dashed 4px var(--v-primary-base);
		color: var(--v-primary-darken4);
		content: 'Drop To Upload';
		display: flex;
		justify-content: center;
		opacity: 0.12;
	}

	&--uploading::before {
		background-color: var(--v-primary-base);
		opacity: 0.04;
	}

	&--uploading::after {
		align-items: center;
		border: solid 4px var(--v-primary-base);
		color: var(--v-primary-darken4);
		display: flex;
		justify-content: center;
		opacity: 0.12;
	}
}

.imageUploader {
	position: relative;
	cursor: pointer;

	&__input {
		position: absolute;
		top: 0;
		left: 0;
		height: 100%;
		width: 100%;
		opacity: 0;
		display: block;
		z-index: 1;
	}

	&__image {
		position: relative;
	}

	&__delete {
		position: absolute;
		top: 8px;
		right: 8px;
	}
}

.cropper-container {
	max-height: 600px;
	max-width: 100%;
	overflow: hidden;
}


.cropper-point.point-se {
	background-color: var(--v-accent-base) !important;
	bottom: -3px;
	cursor: nwse-resize;
	height: 20px;
	opacity: 1;
	right: -3px;
	width: 20px;
}


</style>
